import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { startOfWeek, endOfWeek, parseISO, isWithinInterval, format, subWeeks } from 'date-fns';
import { CheckCircle, AlertCircle, Plus, ChevronDown, ChevronUp, Check, X } from 'lucide-react';

const PAYMENT_METHODS = ['Check', 'ACH', 'Wire', 'Cash', 'Credit Card', 'Other'];

export default function Payments() {
  const { logs, customers, payments, addPayment, getInvoiceNumber } = useApp();
  const [weekOffset, setWeekOffset] = useState(0);
  const [invoiceNums, setInvoiceNums] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [showPayForm, setShowPayForm] = useState(null);
  const [payForm, setPayForm] = useState({});

  const refDate = subWeeks(new Date(), weekOffset);
  const weekStart = startOfWeek(refDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(refDate, { weekStartsOn: 1 });
  const thisWeekKey = format(weekStart, 'yyyy-MM-dd');

  const weekLogs = logs.filter(l => {
    try { return isWithinInterval(parseISO(l.date), { start: weekStart, end: weekEnd }); }
    catch (e) { return false; }
  });

  const byCustomer = customers.map(c => {
    const cLogs = weekLogs.filter(l => l.customerId === c.id);
    const totalHours = cLogs.reduce((sum, l) => sum + parseFloat(l.hours || 0), 0);
    const totalAmount = totalHours * c.hourlyRate;
    const invKey = c.id + '_' + thisWeekKey;
    const invoicePayments = payments.filter(p => p.customerId === c.id && p.weekKey === thisWeekKey);
    const totalPaid = invoicePayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const balance = totalAmount - totalPaid;
    const status = totalAmount === 0 ? 'none' : balance <= 0 ? 'paid' : totalPaid > 0 ? 'partial' : 'unpaid';
    return { ...c, logs: cLogs, totalHours, totalAmount, totalPaid, balance, status, invoiceNum: invoiceNums[invKey], invoicePayments };
  }).filter(c => c.logs.length > 0);

  const totalOutstanding = byCustomer.reduce((sum, c) => sum + Math.max(0, c.balance), 0);
  const totalCollected = byCustomer.reduce((sum, c) => sum + c.totalPaid, 0);

  useEffect(() => {
    byCustomer.forEach(async c => {
      const key = c.id + '_' + thisWeekKey;
      if (!invoiceNums[key]) {
        const num = await getInvoiceNumber(c.id, thisWeekKey);
        setInvoiceNums(prev => ({ ...prev, [key]: num }));
      }
    });
  }, [weekOffset, logs, customers]);

  const handleAddPayment = async (customerId) => {
    const form = payForm[customerId] || {};
    if (!form.amount || parseFloat(form.amount) <= 0) { alert('Please enter a valid amount.'); return; }
    await addPayment({
      customerId,
      weekKey: thisWeekKey,
      amount: parseFloat(form.amount),
      method: form.method || 'Check',
      date: form.date || format(new Date(), 'yyyy-MM-dd'),
      note: form.note || '',
      invoiceNum: invoiceNums[customerId + '_' + thisWeekKey],
    });
    setPayForm(prev => ({ ...prev, [customerId]: {} }));
    setShowPayForm(null);
  };

  const setField = (customerId, field) => (e) => setPayForm(prev => ({ ...prev, [customerId]: { ...(prev[customerId] || {}), [field]: e.target.value } }));

  const statusTag = (status) => {
    if (status === 'paid') return <span className="tag tag-green">Paid</span>;
    if (status === 'partial') return <span className="tag" style={{ background: '#713f12', color: '#fbbf24' }}>Partial</span>;
    return <span className="tag" style={{ background: '#3f1515', color: '#ef4444' }}>Unpaid</span>;
  };

  return (
    <div>
      <div className="grid-2" style={{ marginBottom: 20 }}>
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ background: '#14532d', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <CheckCircle size={20} color="var(--success)" />
          </div>
          <div>
            <div style={{ fontSize: 11, fontFamily: 'Barlow Condensed', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-dim)' }}>Collected This Week</div>
            <div style={{ fontFamily: 'Barlow Condensed', fontWeight: 800, fontSize: 24, color: 'var(--success)' }}>${totalCollected.toFixed(2)}</div>
          </div>
        </div>
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ background: '#3f1515', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <AlertCircle size={20} color="var(--danger)" />
          </div>
          <div>
            <div style={{ fontSize: 11, fontFamily: 'Barlow Condensed', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-dim)' }}>Outstanding This Week</div>
            <div style={{ fontFamily: 'Barlow Condensed', fontWeight: 800, fontSize: 24, color: 'var(--danger)' }}>${totalOutstanding.toFixed(2)}</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ fontSize: 20 }}>Payment Tracking</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-secondary" style={{ padding: '7px 12px', fontSize: 12 }} onClick={() => setWeekOffset(w => w + 1)}>← Prev</button>
          <button className="btn-secondary" style={{ padding: '7px 12px', fontSize: 12 }} disabled={weekOffset === 0} onClick={() => setWeekOffset(w => w - 1)}>Next →</button>
        </div>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>Week of {format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d, yyyy')}</p>

      {byCustomer.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <p style={{ color: 'var(--text-muted)' }}>No invoices for this week.</p>
        </div>
      ) : byCustomer.map(c => (
        <div key={c.id} className="card" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', flexWrap: 'wrap' }}
            onClick={() => setExpanded(expanded === c.id ? null : c.id)}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 17 }}>{c.name}</span>
                {statusTag(c.status)}
                {c.invoiceNum && <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>#{c.invoiceNum}</span>}
              </div>
              <div style={{ marginTop: 4, fontSize: 13, color: 'var(--text-muted)' }}>
                Invoice: <strong style={{ color: 'var(--text)' }}>${c.totalAmount.toFixed(2)}</strong>
                {c.totalPaid > 0 && <> · Paid: <strong style={{ color: 'var(--success)' }}>${c.totalPaid.toFixed(2)}</strong></>}
                {c.balance > 0 && <> · Due: <strong style={{ color: 'var(--danger)' }}>${c.balance.toFixed(2)}</strong></>}
              </div>
            </div>
            {c.balance > 0 && (
              <button className="btn-primary" style={{ padding: '6px 14px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}
                onClick={e => { e.stopPropagation(); setShowPayForm(showPayForm === c.id ? null : c.id); setExpanded(c.id); }}>
                <Plus size={13} /> Record Payment
              </button>
            )}
            {expanded === c.id ? <ChevronUp size={16} color="var(--text-muted)" /> : <ChevronDown size={16} color="var(--text-muted)" />}
          </div>

          {expanded === c.id && (
            <div style={{ borderTop: '1px solid var(--border)', marginTop: 14, paddingTop: 14 }}>
              {showPayForm === c.id && c.balance > 0 && (
                <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 16 }}>
                  <div style={{ fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 13, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>Record Payment</div>
                  <div className="grid-2">
                    <div className="field">
                      <label>Amount ($)</label>
                      <input type="number" step="0.01" min="0" placeholder={c.balance.toFixed(2)} value={(payForm[c.id] || {}).amount || ''} onChange={setField(c.id, 'amount')} />
                    </div>
                    <div className="field">
                      <label>Date</label>
                      <input type="date" value={(payForm[c.id] || {}).date || format(new Date(), 'yyyy-MM-dd')} onChange={setField(c.id, 'date')} />
                    </div>
                  </div>
                  <div className="field">
                    <label>Payment Method</label>
                    <select value={(payForm[c.id] || {}).method || 'Check'} onChange={setField(c.id, 'method')}>
                      {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="field">
                    <label>Note (optional)</label>
                    <input placeholder="e.g. Check #1234" value={(payForm[c.id] || {}).note || ''} onChange={setField(c.id, 'note')} />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn-primary" onClick={() => handleAddPayment(c.id)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Check size={13} /> Save Payment</button>
                    <button className="btn-secondary" onClick={() => setShowPayForm(null)} style={{ padding: '7px 14px' }}>Cancel</button>
                  </div>
                </div>
              )}

              {c.invoicePayments.length > 0 ? (
                <div>
                  <div style={{ fontSize: 11, fontFamily: 'Barlow Condensed', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: 8 }}>Payment History</div>
                  {c.invoicePayments.map(p => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderTop: '1px solid var(--border)' }}>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontFamily: 'Barlow Condensed', fontWeight: 600 }}>${parseFloat(p.amount).toFixed(2)}</span>
                        <span style={{ color: 'var(--text-muted)', fontSize: 13, marginLeft: 10 }}>{p.method}</span>
                        {p.note && <span style={{ color: 'var(--text-dim)', fontSize: 12, marginLeft: 8 }}>— {p.note}</span>}
                      </div>
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{p.date}</span>
                    </div>
                  ))}
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-dim)' }}>Balance Due</span>
                    <span style={{ fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 16, color: c.balance <= 0 ? 'var(--success)' : 'var(--danger)' }}>
                      {c.balance <= 0 ? 'PAID IN FULL' : '$' + c.balance.toFixed(2)}
                    </span>
                  </div>
                </div>
              ) : (
                <p style={{ color: 'var(--text-dim)', fontSize: 13 }}>No payments recorded yet.</p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
